/** @jsxImportSource @emotion/react */
import type { Venue, Facility } from '../../hooks/useSchedulerLocations'
import type {
  TimeSlot,
  CreateTimeSlotData,
  UpdateTimeSlotData,
} from '../../hooks/useSchedulerTimeSlots'
import { getSlotFingerprint } from '../../hooks/useSchedulerTimeSlots'

/**
 * Edit state for a time slot row
 */
export interface TimeSlotEditState {
  slotId: string | null // null for new slot
  facilityId?: string // optional — not needed for venue-level ops
  frequency: 'weekly' | 'biweekly' | 'monthly'
  days: number[] // 0=Sun, 1=Mon, ... 6=Sat
  startTime: string // "HH:MM" 24hr format
  endTime: string // "HH:MM" 24hr format
  divisions: string[] // division IDs
}

/**
 * A group of identical time slots across facilities in a venue.
 * Used to display one row per unique config instead of one row per facility.
 */
export interface VenueTimeSlotGroup {
  fingerprint: string
  representative: TimeSlot // first slot in group — used for display
  slotIds: string[] // all slot IDs across facilities with this config
}

/**
 * Groups time slots belonging to a venue's facilities by fingerprint.
 */
export const getVenueTimeSlotGroups = (
  allTimeSlots: TimeSlot[],
  venue: Venue
): VenueTimeSlotGroup[] => {
  const facilityIds = new Set(venue.facilities?.map((f) => f.id) || [])
  const venueSlots = allTimeSlots.filter((s) => facilityIds.has(s.facility_id))

  const map = new Map<string, VenueTimeSlotGroup>()
  for (const slot of venueSlots) {
    const fp = getSlotFingerprint(slot)
    const existing = map.get(fp)
    if (existing) {
      existing.slotIds.push(slot.id)
    } else {
      map.set(fp, {
        fingerprint: fp,
        representative: slot,
        slotIds: [slot.id],
      })
    }
  }

  return Array.from(map.values())
}

/**
 * Division option for time slot scope
 */
export interface DivisionOption {
  id: string
  name: string
}

/**
 * Props for VenueCard
 */
export interface VenueCardProps {
  venue: Venue
  competitionId: string
  timeSlots: TimeSlot[]
  timeSlotCounts: Record<string, number>
  timeSlotsLoading: boolean
  divisions: DivisionOption[]
  onEditVenue: (venue: Venue) => void
  onDeleteVenue: (venue: Venue) => void
  onDeleteFacility: (venue: Venue, facility: Facility) => void
  onAddTimeSlot: (
    facilityId: string,
    data: CreateTimeSlotData
  ) => Promise<TimeSlot>
  onUpdateTimeSlot: (
    timeSlotId: string,
    data: UpdateTimeSlotData
  ) => Promise<TimeSlot>
  onDeleteTimeSlot: (timeSlotId: string) => Promise<void>
  onDuplicateTimeSlot: (timeSlotId: string) => Promise<TimeSlot>
  getTimeSlotsForFacility: (facilityId: string) => TimeSlot[]
  onAddTimeSlotForVenue: (
    facilityIds: string[],
    data: CreateTimeSlotData
  ) => Promise<void>
  onUpdateTimeSlotBatch: (
    timeSlotIds: string[],
    data: UpdateTimeSlotData
  ) => Promise<void>
  onDeleteTimeSlotBatch: (timeSlotIds: string[]) => Promise<void>
}

/**
 * Props for SurfaceSection
 */
export interface SurfaceSectionProps {
  venue: Venue
  facility: Facility
  competitionId: string
  timeSlots: TimeSlot[]
  timeSlotCount: number
  isExpanded: boolean
  timeSlotsLoading: boolean
  divisions: DivisionOption[]
  onToggle: () => void
  onAddTimeSlot: (
    facilityId: string,
    data: CreateTimeSlotData
  ) => Promise<TimeSlot>
  onUpdateTimeSlot: (
    timeSlotId: string,
    data: UpdateTimeSlotData
  ) => Promise<TimeSlot>
  onDeleteTimeSlot: (timeSlotId: string) => Promise<void>
  onDuplicateTimeSlot: (timeSlotId: string) => Promise<TimeSlot>
  onEditSurface: (venue: Venue, facility: Facility) => void
  onDeleteSurface: (venue: Venue, facility: Facility) => void
}

/**
 * Props for TimeSlotRow
 */
export interface TimeSlotRowProps {
  timeSlot: TimeSlot
  index: number
  isEditing: boolean
  editState: TimeSlotEditState | null
  divisions: DivisionOption[]
  onEdit: () => void
  onSave: () => void
  onCancel: () => void
  onDelete: () => void
  onEditStateChange: (state: TimeSlotEditState) => void
  loading: boolean
}

/**
 * Props for TimeSlotTableHeader
 */
export interface TimeSlotTableHeaderProps {
  // No props needed, pure presentational
}

/**
 * Props for DayOfWeekSelector
 */
export interface DayOfWeekSelectorProps {
  selectedDays: number[]
  onChange: (days: number[]) => void
  disabled?: boolean
}

/**
 * Column widths for consistent table layout
 */
export const COLUMN_WIDTHS = {
  SLOT: 40,
  FREQUENCY: 160,
  DATE: 164,
  DAYS: 272,
  START_TIME: 130,
  END_TIME: 130,
  DURATION: 80,
  DIVISIONS: 180,
  ACTIONS: 89,
} as const
